import { storableError } from '../../util/errors';
import fetch from 'cross-fetch';
import { convertUnitToSubUnit, unitDivisor } from '../../util/currency';
import {
  parseDateFromISO8601,
  getExclusiveEndDate,
  addTime,
  subtractTime,
  daysBetween,
  getStartOf,
} from '../../util/dates';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { isOriginInUse, isStockInUse } from '../../util/search';
import { parse } from '../../util/urlHelpers';
import { apiBaseUrl } from '../../util/api';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { getUserCountry, isUserInEUCountry } from '../../util/dataExtractor';
import { denormalisedResponseEntities } from '../../util/data';

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 12 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
const RESULT_PAGE_SIZE = 8;

// ================ Action types ================ //

export const SEARCH_LISTINGS_REQUEST = 'app/LandingPage/SEARCH_LISTINGS_REQUEST';
export const SEARCH_LISTINGS_SUCCESS = 'app/LandingPage/SEARCH_LISTINGS_SUCCESS';
export const SEARCH_LISTINGS_ERROR = 'app/LandingPage/SEARCH_LISTINGS_ERROR';

export const SEARCH_RECENTLY_LISTED_REQUEST = 'app/LandingPage/SEARCH_RECENTLY_LISTED_REQUEST';
export const SEARCH_RECENTLY_LISTED_SUCCESS = 'app/LandingPage/SEARCH_RECENTLY_LISTED_SUCCESS';
export const SEARCH_RECENTLY_LISTED_ERROR = 'app/LandingPage/SEARCH_RECENTLY_LISTED_ERROR';

export const SEARCH_RECENTLY_SOLD_REQUEST = 'app/LandingPage/SEARCH_RECENTLY_SOLD_REQUEST';
export const SEARCH_RECENTLY_SOLD_SUCCESS = 'app/LandingPage/SEARCH_RECENTLY_SOLD_SUCCESS';
export const SEARCH_RECENTLY_SOLD_ERROR = 'app/LandingPage/SEARCH_RECENTLY_SOLD_ERROR';

export const SEARCH_MAP_LISTINGS_REQUEST = 'app/LandingPage/SEARCH_MAP_LISTINGS_REQUEST';
export const SEARCH_MAP_LISTINGS_SUCCESS = 'app/LandingPage/SEARCH_MAP_LISTINGS_SUCCESS';
export const SEARCH_MAP_LISTINGS_ERROR = 'app/LandingPage/SEARCH_MAP_LISTINGS_ERROR';

export const SEARCH_MAP_SET_ACTIVE_LISTING = 'app/LandingPage/SEARCH_MAP_SET_ACTIVE_LISTING';

// ================ Reducer ================ //

const initialState = {
  pagination: null,
  searchParams: null,
  searchInProgress: false,
  searchListingsError: null,
  currentPageResultIds: [],
  recentlySoldListings: [],
  recentlyListedListings: [],
  recentlyListedInProgress: false,
  recentlyListedError: null,
  recentlySoldInProgress: false,
  recentlySoldError: null,
};

const resultIds = data => data.data.map(l => l.id);

const listingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SEARCH_LISTINGS_REQUEST:
      return {
        ...state,
        searchParams: payload.searchParams,
        searchInProgress: true,
        searchMapListingIds: [],
        searchListingsError: null,
      };
    case SEARCH_LISTINGS_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        searchInProgress: false,
      };
    case SEARCH_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, searchInProgress: false, searchListingsError: payload };

    case SEARCH_RECENTLY_LISTED_REQUEST:
      return { ...state, recentlyListedInProgress: true, recentlyListedError: null };
    case SEARCH_RECENTLY_LISTED_SUCCESS:
      return {
        ...state,
        recentlyListedListings: payload.listings,
        recentlyListedInProgress: false,
      };
    case SEARCH_RECENTLY_LISTED_ERROR:
      return { ...state, recentlyListedInProgress: false, recentlyListedError: payload };

    case SEARCH_RECENTLY_SOLD_REQUEST:
      return { ...state, recentlySoldInProgress: true, recentlySoldError: null };
    case SEARCH_RECENTLY_SOLD_SUCCESS:
      return {
        ...state,
        recentlySoldListings: payload.listings,
        recentlySoldInProgress: false,
      };
    case SEARCH_RECENTLY_SOLD_ERROR:
      return { ...state, recentlySoldInProgress: false, recentlySoldError: payload };

    case SEARCH_MAP_SET_ACTIVE_LISTING:
      return {
        ...state,
        activeListingId: payload,
      };
    default:
      return state;
  }
};

export default listingPageReducer;

// ================ Action creators ================ //

export const searchListingsRequest = searchParams => ({
  type: SEARCH_LISTINGS_REQUEST,
  payload: { searchParams },
});

export const searchListingsSuccess = response => ({
  type: SEARCH_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

export const searchListingsError = e => ({
  type: SEARCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const searchRecentlyListedRequest = () => ({ type: SEARCH_RECENTLY_LISTED_REQUEST });

export const searchRecentlyListedSuccess = listings => ({
  type: SEARCH_RECENTLY_LISTED_SUCCESS,
  payload: { listings },
});

export const searchRecentlyListedError = e => ({
  type: SEARCH_RECENTLY_LISTED_ERROR,
  error: true,
  payload: e,
});

export const searchRecentlySoldRequest = () => ({ type: SEARCH_RECENTLY_SOLD_REQUEST });

export const searchRecentlySoldSuccess = listings => ({
  type: SEARCH_RECENTLY_SOLD_SUCCESS,
  payload: { listings },
});

export const searchRecentlySoldError = e => ({
  type: SEARCH_RECENTLY_SOLD_ERROR,
  error: true,
  payload: e,
});

export const searchListings = (searchParams, config) => (dispatch, getState, sdk) => {
  dispatch(searchListingsRequest(searchParams));

  // SearchPage can enforce listing query to only those listings with valid listingType
  // NOTE: this only works if you have set 'enum' type search schema to listing's public data fields
  //       - listingType
  //       Same setup could be expanded to 2 other extended data fields:
  //       - transactionProcessAlias
  //       - unitType
  //       ...and then turned enforceValidListingType config to true in configListing.js
  // Read More:
  // https://www.sharetribe.com/docs/how-to/manage-search-schemas-with-flex-cli/#adding-listing-search-schemas
  const searchValidListingTypes = listingTypes => {
    return config.listing.enforceValidListingType
      ? {
          pub_listingType: listingTypes.map(l => l.listingType),
          // pub_transactionProcessAlias: listingTypes.map(l => l.transactionType.alias),
          // pub_unitType: listingTypes.map(l => l.transactionType.unitType),
        }
      : {};
  };

  const priceSearchParams = priceParam => {
    const inSubunits = value => convertUnitToSubUnit(value, unitDivisor(config.currency));
    const values = priceParam ? priceParam.split(',') : [];
    return priceParam && values.length === 2
      ? {
          price: [inSubunits(values[0]), inSubunits(values[1]) + 1].join(','),
        }
      : {};
  };

  const datesSearchParams = datesParam => {
    const searchTZ = 'Etc/UTC';
    const datesFilter = config.search.defaultFilters.find(f => f.key === 'dates');
    const values = datesParam ? datesParam.split(',') : [];
    const hasValues = datesFilter && datesParam && values.length === 2;
    const { dateRangeMode, availability } = datesFilter || {};
    const isNightlyMode = dateRangeMode === 'night';
    const isEntireRangeAvailable = availability === 'time-full';

    // SearchPage need to use a single time zone but listings can have different time zones
    // We need to expand/prolong the time window (start & end) to cover other time zones too.
    //
    // NOTE: you might want to consider changing UI so that
    //   1) location is always asked first before date range
    //   2) use some 3rd party service to convert location to time zone (IANA tz name)
    //   3) Make exact dates filtering against that specific time zone
    //   This setup would be better for dates filter,
    //   but it enforces a UX where location is always asked first and therefore configurability
    const getProlongedStart = date => subtractTime(date, 14, 'hours', searchTZ);
    const getProlongedEnd = date => addTime(date, 12, 'hours', searchTZ);

    const startDate = hasValues ? parseDateFromISO8601(values[0], searchTZ) : null;
    const endRaw = hasValues ? parseDateFromISO8601(values[1], searchTZ) : null;
    const endDate =
      hasValues && isNightlyMode
        ? endRaw
        : hasValues
        ? getExclusiveEndDate(endRaw, searchTZ)
        : null;

    const today = getStartOf(new Date(), 'day', searchTZ);
    const possibleStartDate = subtractTime(today, 14, 'hours', searchTZ);
    const hasValidDates =
      hasValues &&
      startDate.getTime() >= possibleStartDate.getTime() &&
      startDate.getTime() <= endDate.getTime();

    const dayCount = isEntireRangeAvailable ? daysBetween(startDate, endDate) : 1;
    const day = 1440;
    const hour = 60;
    // When entire range is required to be available, we count minutes of included date range,
    // but there's a need to subtract one hour due to possibility of daylight saving time.
    // If partial range is needed, then we just make sure that the shortest time unit supported
    // is available within the range.
    // You might want to customize this to match with your time units (e.g. day: 1440 - 60)
    const minDuration = isEntireRangeAvailable ? dayCount * day - hour : hour;
    return hasValidDates
      ? {
          start: getProlongedStart(startDate),
          end: getProlongedEnd(endDate),
          // Availability can be time-full or time-partial.
          // However, due to prolonged time window, we need to use time-partial.
          availability: 'time-partial',
          // minDuration uses minutes
          minDuration,
        }
      : {};
  };

  const { perPage, price, dates, sort, ...rest } = searchParams;
  const priceMaybe = priceSearchParams(price);
  const datesMaybe = datesSearchParams(dates);
  const sortMaybe = sort === config.search.sortConfig.relevanceKey ? {} : { sort };

  const params = {
    ...rest,
    ...priceMaybe,
    ...datesMaybe,
    ...sortMaybe,
    ...searchValidListingTypes(config.listing.listingTypes),
    perPage,
  };

  return sdk.listings
    .query(params)
    .then(response => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };
      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      dispatch(searchListingsSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(searchListingsError(storableError(e)));
      throw e;
    });
};

const isPublishedListing = listing =>
  listing?.attributes?.state === 'published' && !listing?.attributes?.deleted;

const isSoldListing = listing =>
  isPublishedListing(listing) && listing?.currentStock?.attributes?.quantity === 0;

const denormalizeLandingListings = (response, config) => {
  const listingFields = config?.listing?.listingFields;
  const sanitizeConfig = { listingFields };
  const entities = denormalisedResponseEntities(response);

  return {
    entities,
    sanitizeConfig,
  };
};

const fetchRecentlySoldListingIds = async (config, limit = RESULT_PAGE_SIZE) => {
  const query = new URLSearchParams({ limit: String(limit) }).toString();
  const response = await fetch(
    `${apiBaseUrl(config.marketplaceRootURL)}/api/recently-sold-listing-ids?${query}`
  );

  if (!response.ok) {
    throw new Error(`Recently sold listing ID request failed with status ${response.status}`);
  }

  const data = await response.json();
  return data?.data?.listingIds || [];
};

const fetchListingById = async (sdk, id, params) => {
  try {
    return await sdk.listings.show({ ...params, id });
  } catch (e) {
    return null;
  }
};

export const searchRecentlyListed = (params, config) => async (dispatch, getState, sdk) => {
  dispatch(searchRecentlyListedRequest());

  try {
    const response = await sdk.listings.query(params);
    const { entities, sanitizeConfig } = denormalizeLandingListings(response, config);

    dispatch(addMarketplaceEntities(response, sanitizeConfig));
    dispatch(searchRecentlyListedSuccess(entities.filter(isPublishedListing)));
    return response;
  } catch (e) {
    const error = storableError(e);
    dispatch(searchRecentlyListedError(error));
    throw e;
  }
};

export const searchRecentlySold = (params, config) => async (dispatch, getState, sdk) => {
  dispatch(searchRecentlySoldRequest());

  try {
    const listingIds = await fetchRecentlySoldListingIds(config, RESULT_PAGE_SIZE * 3);
    const responses = await Promise.all(
      listingIds.map(listingId => fetchListingById(sdk, listingId, params))
    );
    const listingResponses = responses.filter(Boolean);
    const listingFields = config?.listing?.listingFields;
    const sanitizeConfig = { listingFields };
    const soldListings = listingResponses
      .flatMap(response => denormalisedResponseEntities(response))
      .filter(isSoldListing)
      .slice(0, RESULT_PAGE_SIZE);

    listingResponses.forEach(response =>
      dispatch(addMarketplaceEntities(response, sanitizeConfig))
    );
    dispatch(searchRecentlySoldSuccess(soldListings));
    return listingResponses;
  } catch (e) {
    const error = storableError(e);
    dispatch(searchRecentlySoldError(error));
    throw e;
  }
};

export const setActiveListing = listingId => ({
  type: SEARCH_MAP_SET_ACTIVE_LISTING,
  payload: listingId,
});

export const loadData = (params, search, config) => async (dispatch, getState, sdk) => {
  const queryParams = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  const state = getState();
  const isAuthenticated = state.auth.isAuthenticated;

  // Try to read current user from state first
  let currentUser = state.user?.currentUser;

  // Fetch only if authenticated and not already in state
  if (isAuthenticated && !currentUser) {
    currentUser = await dispatch(fetchCurrentUser({ enforce: true }));
  }

  const userCountry = currentUser ? getUserCountry(currentUser) : null;
  const isEUCountry = currentUser ? isUserInEUCountry(currentUser) : false;

  // Add minStock filter with default value (1), if stock management is in use.
  const minStockMaybe = isStockInUse(config) ? { minStock: 1 } : {};

  const { page = 1, address, origin, ...rest } = queryParams;
  const originMaybe = isOriginInUse(config) && origin ? { origin } : {};

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  const aspectRatio = aspectHeight / aspectWidth;

  const countryMaybe =
    currentUser && isEUCountry
      ? { pub_isEUCountry: true }
      : currentUser
      ? { pub_country: userCountry?.value || userCountry }
      : {};

  const imageFields = [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`];
  const imageVariants = {
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
  };

  const baseParams = {
    ...rest,
    ...originMaybe,
    ...countryMaybe,
    sort: 'createdAt',
    include: ['author', 'images', 'currentStock'],
    'fields.listing': [
      'title',
      'price',
      'deleted',
      'state',
      'publicData.listingType',
      'publicData.transactionProcessAlias',
      'publicData.unitType',
      'publicData.cardStyle',
    ],
    'fields.user': [
      'profile.displayName',
      'profile.abbreviatedName',
      'profile.publicData.username',
    ],
    'fields.image': imageFields,
    ...imageVariants,
    'limit.images': 1,
  };

  const recentlyListedParams = {
    ...baseParams,
    ...minStockMaybe,
    page,
    perPage: RESULT_PAGE_SIZE,
  };

  const { sort, ...listingShowParams } = baseParams;

  return Promise.all([
    dispatch(searchRecentlyListed(recentlyListedParams, config)),
    dispatch(searchRecentlySold(listingShowParams, config)),
  ]);
};
